import React, { useState, useRef, useEffect } from 'react';
import OptionToggle from '../../components/OptionToggle';
import settings from '../../core/settings';
import { getAllSkins, getSkin } from './tileSkins';

function usePersistedSetting(key, value, { serialize, dispatchEvent: eventName } = {}) {
  const initRef = useRef(false);
  useEffect(() => {
    if (!initRef.current) { initRef.current = true; return; }
    try {
      const stored = serialize ? serialize(value) : value;
      if (stored === null || stored === undefined) settings.remove(key);
      else settings.set(key, stored);
      if (eventName) {
        try { window.dispatchEvent(new CustomEvent(eventName, { detail: value })); } catch (e) {}
      }
    } catch (e) {}
  }, [key, value, serialize, eventName]);
}

const serializeBool = (v) => v ? 'true' : 'false';

function TensaiSettingsPanel() {
  const [gridScale, setGridScale] = useState(() => settings.getNumber(settings.KEYS.TENSAI_GRID_SCALE));
  const [clearEffect, setClearEffect] = useState(() => settings.getBool(settings.KEYS.CLEAR_EFFECT));
  const [hideEmptyTiles, setHideEmptyTiles] = useState(() => settings.getBool(settings.KEYS.HIDE_EMPTY_TILES));
  const [showInstructions, setShowInstructions] = useState(() => settings.getBool(settings.KEYS.SHOW_INSTRUCTIONS));
  const [tileSkin, setTileSkin] = useState(() => settings.getString(settings.KEYS.TILE_SKIN));

  usePersistedSetting(settings.KEYS.TENSAI_GRID_SCALE, gridScale, { dispatchEvent: 'tensaiGridScaleChanged' });
  usePersistedSetting(settings.KEYS.CLEAR_EFFECT, clearEffect, { serialize: serializeBool, dispatchEvent: 'clearEffectChanged' });
  usePersistedSetting(settings.KEYS.HIDE_EMPTY_TILES, hideEmptyTiles, { serialize: serializeBool, dispatchEvent: 'hideEmptyTilesChanged' });
  usePersistedSetting(settings.KEYS.SHOW_INSTRUCTIONS, showInstructions, { serialize: serializeBool });
  usePersistedSetting(settings.KEYS.TILE_SKIN, tileSkin, { dispatchEvent: 'tileSkinChanged' });

  const skins = getAllSkins();

  const toggleRows = [
    ['Clear Effect', clearEffect, setClearEffect, 'Toggle clear effect'],
    ['Hide Empty Tiles', hideEmptyTiles, setHideEmptyTiles, 'Toggle hide empty tiles'],
    ['Show Instructions', showInstructions, setShowInstructions, 'Toggle instructions'],
  ];

  return (
    <div className="control-group">
      <label>Graphics</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div className="volume-row">
          <div className="volume-label">Grid Scale</div>
          <input
            className="volume-range"
            type="range"
            min="50"
            max="150"
            value={gridScale}
            onChange={(e) => setGridScale(Number(e.target.value))}
          />
          <div className="volume-value">{gridScale}%</div>
        </div>
        {/* Tile Skin selector */}
        <div className="setting-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
          <div className="setting-label">Tile Skin</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
            {(() => {
              const activeSkin = getSkin(tileSkin);
              const p = activeSkin.preview || {};
              return (
                <div
                  style={{
                    width: 36,
                    height: 36,
                    background: p.bg || '#393c41',
                    border: p.border || '2px solid #fff',
                    borderRadius: p.borderRadius || 8,
                    color: p.color || '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: p.boxShadow || 'none',
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  5
                </div>
              );
            })()}
            <select
              className="setting-select"
              value={tileSkin}
              onChange={(e) => setTileSkin(e.target.value)}
            >
              {skins.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        {toggleRows.map(([label, checked, onChange, ariaLabel]) => (
          <div
            key={label}
            className="setting-row"
            style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}
          >
            <div className="setting-label">{label}</div>
            <div style={{ marginLeft: 'auto' }}>
              <OptionToggle
                checked={checked}
                onChange={(v) => onChange(Boolean(v))}
                ariaLabel={ariaLabel}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TensaiSettingsPanel;
